import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add,
  Delete,
  Edit,
  ExpandMore,
  School,
  CalendarMonth,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { courseAPI } from '../../../services/api';

const CourseEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSemesters, setSavingSemesters] = useState(false);
  const [course, setCourse] = useState(null);
  const [originalSemesters, setOriginalSemesters] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedWarningDialog, setUnsavedWarningDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Course form data
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    description: '',
    durationValue: '',
    durationUnit: 'months',
    numberOfSemesters: 1,
    eligibility: '',
    feesAmount: '',
  });

  // Semesters data
  const [semesters, setSemesters] = useState([]);
  const [categories, setCategories] = useState([]);

  // Dialog states
  const [semesterDialog, setSemesterDialog] = useState(false);
  const [subjectDialog, setSubjectDialog] = useState(false);
  const [blockDialog, setBlockDialog] = useState(false);
  const [editingSemester, setEditingSemester] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [selectedSemesterIndex, setSelectedSemesterIndex] = useState(null);

  // Semester form
  const [semesterForm, setSemesterForm] = useState({
    semesterNumber: 1,
    name: '',
    startMonth: 1,
    endMonth: 6,
    durationMonths: 6,
  });

  // Subject form
  const [subjectForm, setSubjectForm] = useState({
    name: '',
    code: '',
    credits: 0,
    hoursPerWeek: 0,
    description: '',
  });

  useEffect(() => {
    fetchCourse();
    fetchCategories();
  }, [id]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(semesters) !== JSON.stringify(originalSemesters);
    setHasUnsavedChanges(hasChanges);
  }, [semesters, originalSemesters]);

  // Warn before page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getOne(id);
      // Handle both nested and direct data structure
      const courseData = response.data.data?.course || response.data.data || response.data;

      console.log('Course data:', courseData); // Debug log

      setCourse(courseData);

      setFormData({
        name: courseData.name || '',
        code: courseData.code || '',
        category: courseData.category?._id || courseData.category || '',
        description: courseData.description || '',
        durationValue: courseData.duration?.value || '',
        durationUnit: courseData.duration?.unit || 'months',
        numberOfSemesters: courseData.numberOfSemesters || 1,
        eligibility: courseData.eligibility || '',
        feesAmount: courseData.fees?.amount || '',
      });

      const semestersData = courseData.semesters || [];
      setSemesters(semestersData);
      setOriginalSemesters(JSON.parse(JSON.stringify(semestersData))); // Deep clone
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await courseAPI.getCategories();
      setCategories(response.data.data?.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.category || !formData.durationValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: formData.name,
        code: formData.code,
        category: formData.category,
        description: formData.description,
        duration: {
          value: Number(formData.durationValue),
          unit: formData.durationUnit,
        },
        numberOfSemesters: Number(formData.numberOfSemesters),
        semesters: semesters,
        eligibility: formData.eligibility,
        fees: {
          amount: Number(formData.feesAmount) || 0,
          currency: 'INR',
        },
      };

      await courseAPI.update(id, payload);
      toast.success('Course updated successfully');
      fetchCourse();
    } catch (error) {
      console.error('Error updating course:', error);
      toast.error(error.response?.data?.message || 'Failed to update course');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBlock = async () => {
    try {
      const newStatus = !course.isActive;
      await courseAPI.update(id, { isActive: newStatus });
      toast.success(`Course ${newStatus ? 'activated' : 'blocked'} successfully`);
      setBlockDialog(false);
      fetchCourse();
    } catch (error) {
      console.error('Error toggling course status:', error);
      toast.error('Failed to update course status');
    }
  };

  // Semester management
  const handleAddSemester = () => {
    setEditingSemester(null);
    setSemesterForm({
      semesterNumber: semesters.length + 1,
      name: `Semester ${semesters.length + 1}`,
      startMonth: 1,
      endMonth: 6,
      durationMonths: 6,
    });
    setSemesterDialog(true);
  };

  const handleEditSemester = (index) => {
    setEditingSemester(index);
    const sem = semesters[index];
    setSemesterForm({
      semesterNumber: sem.semesterNumber,
      name: sem.name,
      startMonth: sem.startMonth || 1,
      endMonth: sem.endMonth || 6,
      durationMonths: sem.durationMonths || 6,
    });
    setSemesterDialog(true);
  };

  const handleSaveSemester = () => {
    if (!semesterForm.name) {
      toast.error('Semester name is required');
      return;
    }

    const newSemester = {
      ...semesterForm,
      semesterNumber: Number(semesterForm.semesterNumber),
      startMonth: Number(semesterForm.startMonth),
      endMonth: Number(semesterForm.endMonth),
      durationMonths: Number(semesterForm.durationMonths),
      subjects: editingSemester !== null ? semesters[editingSemester].subjects : [],
      isActive: true,
    };

    if (editingSemester !== null) {
      const updated = [...semesters];
      updated[editingSemester] = newSemester;
      setSemesters(updated);
    } else {
      setSemesters([...semesters, newSemester]);
    }

    setSemesterDialog(false);
  };

  const handleDeleteSemester = (index) => {
    const updated = semesters.filter((_, i) => i !== index);
    setSemesters(updated);
  };

  const handleSaveSemestersToDb = async () => {
    try {
      setSavingSemesters(true);

      const payload = {
        semesters: semesters,
      };

      await courseAPI.update(id, payload);
      toast.success('Semesters and subjects saved successfully');
      setOriginalSemesters(JSON.parse(JSON.stringify(semesters))); // Update original after save
      setHasUnsavedChanges(false);
      fetchCourse();
    } catch (error) {
      console.error('Error saving semesters:', error);
      toast.error(error.response?.data?.message || 'Failed to save semesters');
    } finally {
      setSavingSemesters(false);
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation('/admin/courses');
      setUnsavedWarningDialog(true);
    } else {
      navigate('/admin/courses');
    }
  };

  const handleSaveAndNavigate = async () => {
    await handleSaveSemestersToDb();
    setUnsavedWarningDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const handleDiscardAndNavigate = () => {
    setHasUnsavedChanges(false);
    setUnsavedWarningDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  // Subject management
  const handleAddSubject = (semesterIndex) => {
    setSelectedSemesterIndex(semesterIndex);
    setEditingSubject(null);
    setSubjectForm({
      name: '',
      code: '',
      credits: 0,
      hoursPerWeek: 0,
      description: '',
    });
    setSubjectDialog(true);
  };

  const handleEditSubject = (semesterIndex, subjectIndex) => {
    setSelectedSemesterIndex(semesterIndex);
    setEditingSubject(subjectIndex);
    const subject = semesters[semesterIndex].subjects[subjectIndex];
    setSubjectForm({
      name: subject.name,
      code: subject.code,
      credits: subject.credits,
      hoursPerWeek: subject.hoursPerWeek,
      description: subject.description || '',
    });
    setSubjectDialog(true);
  };

  const handleSaveSubject = () => {
    if (!subjectForm.name || !subjectForm.code) {
      toast.error('Subject name and code are required');
      return;
    }

    const newSubject = {
      ...subjectForm,
      credits: Number(subjectForm.credits),
      hoursPerWeek: Number(subjectForm.hoursPerWeek),
      isActive: true,
    };

    const updated = [...semesters];
    if (!updated[selectedSemesterIndex].subjects) {
      updated[selectedSemesterIndex].subjects = [];
    }

    if (editingSubject !== null) {
      updated[selectedSemesterIndex].subjects[editingSubject] = newSubject;
    } else {
      updated[selectedSemesterIndex].subjects.push(newSubject);
    }

    setSemesters(updated);
    setSubjectDialog(false);
  };

  const handleDeleteSubject = (semesterIndex, subjectIndex) => {
    const updated = [...semesters];
    updated[semesterIndex].subjects = updated[semesterIndex].subjects.filter(
      (_, i) => i !== subjectIndex
    );
    setSemesters(updated);
  };

  const getMonthName = (monthNum) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1] || '';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!course) {
    return (
      <Box>
        <Alert severity="error">Course not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Edit Course
          </Typography>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mt: 1 }}>
            {course.code}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={course.isActive ? 'outlined' : 'contained'}
            color={course.isActive ? 'error' : 'success'}
            startIcon={course.isActive ? <Block /> : <CheckCircle />}
            onClick={() => setBlockDialog(true)}
          >
            {course.isActive ? 'Block Course' : 'Activate Course'}
          </Button>
          <Button startIcon={<ArrowBack />} onClick={handleNavigateBack}>
            Back to Courses
          </Button>
        </Box>
      </Box>

      {/* Course Status */}
      {!course.isActive && (
        <Alert severity="error" sx={{ mb: 3 }}>
          This course is currently <strong>BLOCKED</strong> and not available for enrollment.
        </Alert>
      )}

      {/* Course Details Form */}
      <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Course Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Course Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Course Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{ minWidth: 250 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Duration"
                name="durationValue"
                type="number"
                value={formData.durationValue}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth required sx={{ minWidth: 250 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  name="durationUnit"
                  value={formData.durationUnit}
                  onChange={handleChange}
                  label="Unit"
                >
                  <MenuItem value="months">Months</MenuItem>
                  <MenuItem value="years">Years</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Number of Semesters"
                name="numberOfSemesters"
                type="number"
                value={formData.numberOfSemesters}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Course Fees (INR)"
                name="feesAmount"
                type="number"
                value={formData.feesAmount}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Eligibility Criteria"
                name="eligibility"
                value={formData.eligibility}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., 10+2 or equivalent"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={handleNavigateBack} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" startIcon={<Save />} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Course'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Semester Management */}
      <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Semesters & Subjects
            </Typography>
            {hasUnsavedChanges && (
              <Chip label="Unsaved Changes" color="warning" size="small" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={handleAddSemester}>
              Add Semester
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Save />}
              onClick={handleSaveSemestersToDb}
              disabled={savingSemesters || semesters.length === 0}
            >
              {savingSemesters ? 'Saving...' : 'Save Semesters & Subjects'}
            </Button>
          </Box>
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Important:</strong> After adding or modifying semesters and subjects, click the "Save Semesters & Subjects" button to save changes to the database.
        </Alert>

        {semesters.length === 0 ? (
          <Alert severity="info">
            No semesters added yet. Click "Add Semester" to create the course structure.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {semesters.map((semester, semIndex) => (
              <Accordion key={semIndex} defaultExpanded={semIndex === 0}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <CalendarMonth color="primary" />
                    <Typography sx={{ fontWeight: 600 }}>{semester.name}</Typography>
                    <Chip
                      size="small"
                      label={`${semester.subjects?.length || 0} Subjects`}
                      color="primary"
                      variant="outlined"
                    />
                    {semester.startMonth && semester.endMonth && (
                      <Chip
                        size="small"
                        label={`${getMonthName(semester.startMonth)} - ${getMonthName(semester.endMonth)}`}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Duration: {semester.durationMonths} months
                      </Typography>
                      <Box>
                        <IconButton size="small" color="primary" onClick={() => handleEditSemester(semIndex)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteSemester(semIndex)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Subjects Table */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Subjects
                        </Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddSubject(semIndex)}>
                          Add Subject
                        </Button>
                      </Box>

                      {semester.subjects && semester.subjects.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Subject Code</TableCell>
                                <TableCell>Subject Name</TableCell>
                                <TableCell align="center">Credits</TableCell>
                                <TableCell align="center">Hours/Week</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {semester.subjects.map((subject, subIndex) => (
                                <TableRow key={subIndex}>
                                  <TableCell>{subject.code}</TableCell>
                                  <TableCell>{subject.name}</TableCell>
                                  <TableCell align="center">{subject.credits}</TableCell>
                                  <TableCell align="center">{subject.hoursPerWeek}</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleEditSubject(semIndex, subIndex)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteSubject(semIndex, subIndex)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="info">No subjects added yet</Alert>
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}

        {/* Bottom Save Button */}
        {semesters.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Save />}
              onClick={handleSaveSemestersToDb}
              disabled={savingSemesters}
            >
              {savingSemesters ? 'Saving...' : 'Save Semesters & Subjects'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Block/Unblock Dialog */}
      <Dialog open={blockDialog} onClose={() => setBlockDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {course.isActive ? 'Block Course' : 'Activate Course'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={course.isActive ? 'warning' : 'info'} sx={{ mt: 2 }}>
            {course.isActive
              ? 'Blocking this course will prevent new student enrollments. Existing students will not be affected.'
              : 'Activating this course will make it available for new enrollments.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialog(false)}>Cancel</Button>
          <Button
            onClick={handleToggleBlock}
            variant="contained"
            color={course.isActive ? 'error' : 'success'}
          >
            {course.isActive ? 'Block Course' : 'Activate Course'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Semester Dialog */}
      <Dialog open={semesterDialog} onClose={() => setSemesterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSemester !== null ? 'Edit Semester' : 'Add Semester'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Semester Number"
                type="number"
                value={semesterForm.semesterNumber}
                onChange={(e) => setSemesterForm({ ...semesterForm, semesterNumber: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Semester Name"
                value={semesterForm.name}
                onChange={(e) => setSemesterForm({ ...semesterForm, name: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Start Month</InputLabel>
                <Select
                  value={semesterForm.startMonth}
                  onChange={(e) => setSemesterForm({ ...semesterForm, startMonth: e.target.value })}
                  label="Start Month"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <MenuItem key={month} value={month}>
                      {getMonthName(month)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>End Month</InputLabel>
                <Select
                  value={semesterForm.endMonth}
                  onChange={(e) => setSemesterForm({ ...semesterForm, endMonth: e.target.value })}
                  label="End Month"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <MenuItem key={month} value={month}>
                      {getMonthName(month)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Duration (Months)"
                type="number"
                value={semesterForm.durationMonths}
                onChange={(e) => setSemesterForm({ ...semesterForm, durationMonths: e.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSemesterDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSemester} variant="contained">
            {editingSemester !== null ? 'Update' : 'Add'} Semester
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Subject Dialog */}
      <Dialog open={subjectDialog} onClose={() => setSubjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSubject !== null ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subject Code"
                value={subjectForm.code}
                onChange={(e) => setSubjectForm({ ...subjectForm, code: e.target.value.toUpperCase() })}
                fullWidth
                required
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subject Name"
                value={subjectForm.name}
                onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Credits"
                type="number"
                value={subjectForm.credits}
                onChange={(e) => setSubjectForm({ ...subjectForm, credits: e.target.value })}
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hours Per Week"
                type="number"
                value={subjectForm.hoursPerWeek}
                onChange={(e) => setSubjectForm({ ...subjectForm, hoursPerWeek: e.target.value })}
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={subjectForm.description}
                onChange={(e) => setSubjectForm({ ...subjectForm, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubjectDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSubject} variant="contained">
            {editingSubject !== null ? 'Update' : 'Add'} Subject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog open={unsavedWarningDialog} onClose={() => setUnsavedWarningDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2 }}>
            You have unsaved changes to semesters and subjects. What would you like to do?
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setUnsavedWarningDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDiscardAndNavigate} color="error" variant="outlined">
            Discard Changes
          </Button>
          <Button onClick={handleSaveAndNavigate} variant="contained" color="success" startIcon={<Save />}>
            Save & Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseEdit;
